import React, { ComponentType, ForwardedRef } from 'react';

import type { SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';

import { ThemeSpacing, ThemeTextSizes } from '../../styles';
import {
  FlexboxSystemProps,
  LayoutSystemProps,
  MarginSystemProps,
  PaddingSystemProps,
  SizeSystemType,
  StringSystemType,
} from '../styledSystemPropTypes';

/** Button size options */
export type ButtonSizes = 'small' | 'default' | 'large';
/** Button visual style variants */
export type ButtonVariants = 'default' | 'primary' | 'flat';
/** Generic type mapping button sizes to values */
export type ButtonSizeMap<T> = {
  small: T;
  default: T;
  large: T;
};

// Height of the button is based off of the line height plus top and bottom padding
export const ButtonYPaddingMap: ButtonSizeMap<ThemeSpacing> = {
  small: '2x',
  default: '3x',
  large: '5x',
};

export const ButtonXPaddingMap: ButtonSizeMap<ThemeSpacing> = {
  small: '4x',
  default: '6x',
  large: '8x',
};

export const ButtonSizeTextSizeMap: ButtonSizeMap<ThemeTextSizes> = {
  small: '100',
  default: '200',
  large: '300',
};

export const ButtonIconSizeMap: ButtonSizeMap<string> = {
  small: '16px',
  default: '24px',
  large: '24px',
};

export const ButtonIconMarginMap: ButtonSizeMap<ThemeSpacing> = {
  small: '2x',
  default: '3x',
  large: '4x',
};

type InternalSubVariants = 'base' | 'contrast' | 'destructive' | 'contrastDestructive';
type InternalVariantWithSubVariant = `${ButtonVariants}.${InternalSubVariants}`;
type InternalVariantWithSubVariantWithPressed = `${InternalVariantWithSubVariant}_pressed`;

export type InternalButtonVariants =
  | 'disabled'
  | InternalVariantWithSubVariant
  | InternalVariantWithSubVariantWithPressed;

export type ButtonCommonProps = MarginSystemProps &
  PaddingSystemProps &
  LayoutSystemProps &
  FlexboxSystemProps & {
    /** Align button within its container */
    alignSelf?: StringSystemType;
    /** Button text content */
    children?: React.ReactNode;
    /** Custom gap between button children, overrides default spacing */
    childrenFlexGap?: string;
    /** Make button circular (equal padding on all sides) */
    circular?: boolean;
    /** Use contrast colors for button on dark backgrounds */
    contrast?: boolean;
    /** Apply destructive/warning styling (typically red) */
    destructive?: boolean;
    /** Disable button interactions */
    disabled?: boolean;
    /** Make button fill full width of container */
    fullWidth?: boolean;
    /** URL to navigate to when button is clicked, turns button into a link */
    href?: string;
    /** (WEB ONLY) <a> tag target attribute, only works if href is set */
    hrefTarget?: '_blank' | '_self' | '_parent' | '_top';
    /** Icon component to display alongside button text */
    icon?: ComponentType<any>;
    /** Position of icon relative to text */
    iconAlignment?: 'left' | 'right';
    /** Show button in selected state, requires ARIA state props */
    isSelected?: boolean;
    /** Show loading spinner */
    loading?: boolean;
    /** Alternative icon to show when isSelected is true */
    selectedIcon?: ComponentType<any>;
    /** Button size preset */
    size?: ButtonSizes;
    /** Visual style variant */
    variant?: ButtonVariants;
    /** Button width */
    width?: SizeSystemType;
    /** Horizontal alignment of button content */
    justifyContent?: StringSystemType;
    /** Theme-aware custom style overrides */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;

    /** @deprecated use hrefTarget instead */
    target?: never;

    /** (WEB ONLY) When href is passed, this sets the <a> tag rel attribute */
    rel?: string;
    /** ID for finding this element in unit tests */
    testID?: string;

    /** React ref for the underlying button element */
    webRef?: ForwardedRef<HTMLButtonElement>;
    /** React ref for the underlying button element */
    rxnRef?: ForwardedRef<React.ElementType>;

    /** AVOID USING, supports some questionable edge cases */
    roleOverride?: 'button' | 'link';
    /** AVOID USING, custom font weight override */
    fontWeight?: string;
    /** DO NOT USE, Override the underlying element type */
    as?: never;
  };

export function getVariant(
  disabled: boolean,
  destructive: boolean,
  contrast: boolean,
  variant: ButtonVariants = 'default',
  // only used on native to provide press styles
  isPressed: boolean = false
): InternalButtonVariants {
  // disabled styles have top priority and no pressed state
  if (disabled) return 'disabled';

  let resolvedVariant: InternalVariantWithSubVariant = `${variant}.base`;
  if (contrast && destructive) {
    resolvedVariant = `${variant}.contrastDestructive`;
  } else if (contrast) {
    resolvedVariant = `${variant}.contrast`;
  } else if (destructive) {
    resolvedVariant = `${variant}.destructive`;
  }

  // if pressed, append `_pressed` to the resolved variant.  Should only happen on native!
  return isPressed ? `${resolvedVariant}_pressed` : resolvedVariant;
}

export function getXPadding(size: ButtonSizes, circular: boolean): ThemeSpacing {
  return circular ? ButtonYPaddingMap[size] : ButtonXPaddingMap[size];
}

export function getFullWidthPropForContainer(fullWidth: boolean):
  | {
      width: '100%';
    }
  | {
      alignSelf: 'center';
    } {
  return fullWidth
    ? {
        width: '100%',
      }
    : {
        alignSelf: 'center',
      };
}
